import { campaignsApi } from 'Api/Campaigns/campaignsApi';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { setIsProceedToNextPage } from '../../../store/config';
import { setIsProceedToPrevPage } from '../../../store/config';
import { useCallback, useContext } from 'react';
import { DocumentTypes } from '@urbanx/agx-ui-components';
import { AuthContext } from '@urbanx/agx.auth.client';

type UploadProgressHandler = (uploadProgress: any) => void;
type UploadFileOptions = {
  contractId?: string;
  onUploadProgressChanged?: UploadProgressHandler | null;
  abortSignal?: AbortSignal;
};
type UploadFileOptionsOrProgress =
  | UploadFileOptions
  | UploadProgressHandler
  | AbortSignal
  | null
  | undefined;
type UploadFile = {
  (
    authToken: string,
    file: any,
    campaignId: string,
    documentType: DocumentTypes,
    options?: UploadFileOptions
  ): Promise<any>;
  (
    authToken: string,
    file: any,
    campaignId: string,
    documentType: DocumentTypes,
    onUploadProgressChanged?: UploadProgressHandler | null,
    abortSignal?: AbortSignal | null
  ): Promise<any>;
};

const isUploadProgressHandler = (
  value: UploadFileOptionsOrProgress
): value is UploadProgressHandler => typeof value === 'function';

const isAbortSignal = (value: unknown): value is AbortSignal =>
  typeof value === 'object' &&
  value !== null &&
  'aborted' in value &&
  typeof (value as AbortSignal).addEventListener === 'function';

const isUploadFileOptions = (value: unknown): value is UploadFileOptions =>
  typeof value === 'object' && value !== null && !isAbortSignal(value);

const resolveUploadArguments = (
  optionsOrProgress?: UploadFileOptionsOrProgress,
  legacyAbortSignal?: AbortSignal | null
) => {
  let contractId: string | undefined;
  let onUploadProgressChanged: UploadProgressHandler | null = null;
  let resolvedAbortSignal: AbortSignal | undefined;

  if (isUploadFileOptions(optionsOrProgress)) {
    contractId = optionsOrProgress.contractId;
    onUploadProgressChanged = optionsOrProgress.onUploadProgressChanged ?? null;
    resolvedAbortSignal = optionsOrProgress.abortSignal;
  } else if (isUploadProgressHandler(optionsOrProgress)) {
    onUploadProgressChanged = optionsOrProgress;
  } else if (isAbortSignal(optionsOrProgress)) {
    resolvedAbortSignal = optionsOrProgress;
  }

  resolvedAbortSignal ??= legacyAbortSignal ?? undefined;

  return {
    contractId,
    onUploadProgressChanged,
    abortSignal: resolvedAbortSignal,
  };
};

export const requestGetFileLink = async (
  authToken: string,
  filePath: string
): Promise<string | null> => {
  try {
    const { data: result } = await campaignsApi(authToken).get<string>('GetUploadedFileLink', {
      filePath,
    });

    return result;
  } catch (err: any) {
    console.error(err);
  }

  return null;
};

export const useGetFileLink = () => {
  const { getAuthToken } = useContext(AuthContext);

  const getFileLink = useCallback(
    async (filePath?: string | null): Promise<string | null> => {
      if (!filePath) return null;

      try {
        const token = await getAuthToken();

        if (!token) return null;

        return await requestGetFileLink(token, filePath);
      } catch (err: any) {
        console.error(err);
      }

      return null;
    },
    [getAuthToken]
  );

  return getFileLink;
};

export const deleteFile = async (authToken: string, filePath: string) => {
  try {
    const { status } = await campaignsApi(authToken).post('RemoveFile', {
      filePath,
    });
    return status;
  } catch (err: any) {
    console.error(err);
  }
};

export const useUploadFile = () => {
  const dispatch = useAppDispatch();
  const progress = ({ loaded, total }: { loaded: number; total: number }) => {
    if (loaded !== total) {
      dispatch(setIsProceedToNextPage(false));
      dispatch(setIsProceedToPrevPage(false));
    }
  };

  const uploadFile: UploadFile = async (
    authToken: string,
    file: any,
    campaignId: string,
    documentType: DocumentTypes,
    optionsOrProgress?: UploadFileOptionsOrProgress,
    legacyAbortSignal: AbortSignal | null = null
  ) => {
    const {
      contractId,
      onUploadProgressChanged,
      abortSignal: resolvedAbortSignal,
    } = resolveUploadArguments(optionsOrProgress, legacyAbortSignal);

    try {
      const { data: result } = await campaignsApi(authToken).post(
        'AddOrUpdateDocument',
        file,
        {
          ContractId: contractId,
          CampaignId: campaignId,
          DocumentType: documentType,
        },
        resolvedAbortSignal,
        (uploadProgress) => {
          progress(uploadProgress);
          if (onUploadProgressChanged != null) {
            onUploadProgressChanged(uploadProgress);
          }
        }
      );

      dispatch(setIsProceedToNextPage(true));
      dispatch(setIsProceedToPrevPage(true));

      return result;
    } catch (err: any) {
      console.error(err);
      dispatch(setIsProceedToNextPage(true));
      dispatch(setIsProceedToPrevPage(true));
    }
  };
  return uploadFile;
};
